import React, { useState } from "react";
import { FiPlus, FiTrash2, FiCheckCircle, FiStar, FiAward, FiShield, FiClock, FiHeart, FiThumbsUp, FiGlobe, FiZap, FiTarget, FiUsers, FiTrendingUp, FiGrid, FiList } from "react-icons/fi";
import { Sparkles, Loader2 } from "lucide-react";
import api from "../../../services/api";

const AVAILABLE_ICONS = [
  { id: 'check', icon: FiCheckCircle, label: 'Check' },
  { id: 'star', icon: FiStar, label: 'Star' },
  { id: 'award', icon: FiAward, label: 'Award' },
  { id: 'shield', icon: FiShield, label: 'Shield' },
  { id: 'clock', icon: FiClock, label: 'Clock' },
  { id: 'heart', icon: FiHeart, label: 'Heart' },
  { id: 'thumbs', icon: FiThumbsUp, label: 'Thumbs Up' },
  { id: 'globe', icon: FiGlobe, label: 'Globe' },
  { id: 'zap', icon: FiZap, label: 'Lightning' },
  { id: 'target', icon: FiTarget, label: 'Target' },
  { id: 'users', icon: FiUsers, label: 'Users' },
  { id: 'trending', icon: FiTrendingUp, label: 'Trending' },
  { id: 'custom', icon: null, label: 'Custom Emoji' },
];

// Add spin animation style
const spinKeyframes = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const FeatureHighlights = ({ features = [], onChange, layout = 'grid', onLayoutChange }) => {
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerateAI = async () => {
    setIsGenerating(true);
    try {
      const response = await api.post('/home/generate-features/');
      if (response.data.success && response.data.features) {
        onChange(response.data.features);
      }
    } catch (error) {
      console.error('Error generating features:', error);
      alert('Failed to generate features. Make sure you have uploaded a CV first.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleAddFeature = () => {
    const newFeature = {
      id: Date.now(),
      text: '',
      icon: 'check',
      customIcon: '',
    };
    onChange([...features, newFeature]);
  };

  const handleRemoveFeature = (id) => {
    onChange(features.filter(f => f.id !== id));
  };

  const handleUpdateFeature = (id, field, value) => {
    onChange(features.map(f =>
      f.id === id ? { ...f, [field]: value } : f
    ));
  };

  const getIconComponent = (iconId) => {
    const found = AVAILABLE_ICONS.find(i => i.id === iconId);
    return found ? found.icon : FiCheckCircle;
  };

  const currentLayout = layout || 'grid';

  return (
    <div style={{
      background: 'linear-gradient(135deg, #064e3b 0%, #065f46 100%)',
      borderRadius: '16px',
      padding: '24px',
      border: '1px solid #059669'
    }}>
      <style>{spinKeyframes}</style>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ padding: '8px', backgroundColor: 'rgba(16, 185, 129, 0.2)', borderRadius: '8px' }}>
            <FiStar style={{ width: '20px', height: '20px', color: '#10b981' }} />
          </div>
          <div>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#ffffff', margin: 0 }}>Feature Highlights</h3>
            <p style={{ fontSize: '12px', color: '#a7f3d0', margin: 0 }}>Add key selling points with icons</p>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Layout Toggle */}
          {onLayoutChange && (
            <div style={{ display: 'flex', backgroundColor: '#0f172a', borderRadius: '8px', padding: '4px' }}>
              <button
                type="button"
                onClick={() => onLayoutChange('grid')}
                style={{
                  padding: '6px 10px',
                  backgroundColor: currentLayout === 'grid' ? '#10b981' : 'transparent',
                  border: 'none',
                  borderRadius: '6px',
                  color: currentLayout === 'grid' ? '#000000' : '#ffffff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '12px',
                  fontWeight: '600'
                }}
              >
                <FiGrid style={{ width: '14px', height: '14px' }} />
                Grid
              </button>
              <button
                type="button"
                onClick={() => onLayoutChange('list')}
                style={{
                  padding: '6px 10px',
                  backgroundColor: currentLayout === 'list' ? '#10b981' : 'transparent',
                  border: 'none',
                  borderRadius: '6px',
                  color: currentLayout === 'list' ? '#000000' : '#ffffff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '12px',
                  fontWeight: '600'
                }}
              >
                <FiList style={{ width: '14px', height: '14px' }} />
                List
              </button>
            </div>
          )}

          {/* AI Generate Button */}
          <button
            type="button"
            onClick={handleGenerateAI}
            disabled={isGenerating}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 18px',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: isGenerating ? 'wait' : 'pointer',
              opacity: isGenerating ? 0.7 : 1
            }}
          >
            {isGenerating ? (
              <>
                <Loader2 style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }} />
                <span>Generating...</span>
              </>
            ) : (
              <>
                <Sparkles style={{ width: '16px', height: '16px' }} />
                <span>AI Generate</span>
              </>
            )}
          </button>

          <button
            type="button"
            onClick={handleAddFeature}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 18px',
              backgroundColor: '#10b981',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            <FiPlus style={{ width: '16px', height: '16px' }} />
            <span>Add Feature</span>
          </button>
        </div>
      </div>

      {features.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '32px',
          border: '2px dashed #059669',
          borderRadius: '12px'
        }}>
          <FiStar style={{ width: '32px', height: '32px', color: '#059669', margin: '0 auto 8px' }} />
          <p style={{ color: '#a7f3d0', fontSize: '14px', margin: 0 }}>No feature highlights yet</p>
          <p style={{ color: '#6ee7b7', fontSize: '12px', marginTop: '4px' }}>Add highlights like "23+ Years Experience"</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {features.map((feature, index) => {
            const IconComponent = getIconComponent(feature.icon);
            const isCustomIcon = feature.icon === 'custom';
            return (
              <div
                key={feature.id}
                style={{
                  backgroundColor: '#0f172a',
                  borderRadius: '12px',
                  padding: '16px',
                  border: '1px solid #334155'
                }}
              >
                {/* Row 1: Icon selector and delete */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  {/* Icon Preview */}
                  <div style={{
                    padding: '12px',
                    backgroundColor: 'rgba(16, 185, 129, 0.2)',
                    borderRadius: '10px',
                    flexShrink: 0
                  }}>
                    {isCustomIcon && feature.customIcon ? (
                      <span style={{ fontSize: '24px' }}>{feature.customIcon}</span>
                    ) : IconComponent ? (
                      <IconComponent style={{ width: '24px', height: '24px', color: '#10b981' }} />
                    ) : (
                      <span style={{ fontSize: '24px' }}>⭐</span>
                    )}
                  </div>

                  {/* Icon Selector */}
                  <select
                    value={feature.icon || 'check'}
                    onChange={(e) => handleUpdateFeature(feature.id, 'icon', e.target.value)}
                    style={{
                      padding: '10px 14px',
                      fontSize: '14px',
                      backgroundColor: '#1e293b',
                      border: '1px solid #475569',
                      borderRadius: '8px',
                      color: '#ffffff',
                      cursor: 'pointer',
                      minWidth: '140px'
                    }}
                  >
                    {AVAILABLE_ICONS.map(icon => (
                      <option key={icon.id} value={icon.id}>{icon.label}</option>
                    ))}
                  </select>

                  {/* Custom Emoji Input */}
                  {isCustomIcon && (
                    <input
                      type="text"
                      value={feature.customIcon || ''}
                      onChange={(e) => handleUpdateFeature(feature.id, 'customIcon', e.target.value)}
                      placeholder="Emoji"
                      maxLength={2}
                      style={{
                        width: '70px',
                        padding: '10px',
                        fontSize: '20px',
                        backgroundColor: '#1e293b',
                        border: '1px solid #475569',
                        borderRadius: '8px',
                        color: '#ffffff',
                        textAlign: 'center'
                      }}
                    />
                  )}

                  {/* Spacer */}
                  <div style={{ flex: 1 }} />

                  {/* Delete Button */}
                  <button
                    type="button"
                    onClick={() => handleRemoveFeature(feature.id)}
                    style={{
                      padding: '10px',
                      backgroundColor: 'rgba(239, 68, 68, 0.1)',
                      border: '1px solid #ef4444',
                      borderRadius: '8px',
                      color: '#ef4444',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '13px',
                      fontWeight: '500'
                    }}
                  >
                    <FiTrash2 style={{ width: '16px', height: '16px' }} />
                    <span>Delete</span>
                  </button>
                </div>

                {/* Row 2: Text Input (full width) */}
                <input
                  type="text"
                  value={feature.text || ''}
                  onChange={(e) => handleUpdateFeature(feature.id, 'text', e.target.value)}
                  placeholder="Enter feature text (e.g., 23+ Years of Experience)"
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    fontSize: '16px',
                    fontWeight: '500',
                    backgroundColor: '#0f172a',
                    border: '1px solid #334155',
                    borderRadius: '8px',
                    color: '#f1f5f9',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            );
          })}
        </div>
      )}

      {/* Preview */}
      {features.length > 0 && features.some(f => f.text) && (
        <div style={{
          marginTop: '20px',
          padding: '20px',
          backgroundColor: '#0f172a',
          borderRadius: '12px',
          border: '1px solid #334155'
        }}>
          <p style={{ fontSize: '12px', color: '#64748b', marginBottom: '16px' }}>Preview (as shown on your site):</p>
          <div style={{
            display: currentLayout === 'grid' ? 'grid' : 'flex',
            gridTemplateColumns: currentLayout === 'grid' ? 'repeat(2, 1fr)' : undefined,
            flexDirection: currentLayout === 'list' ? 'column' : undefined,
            gap: '16px'
          }}>
            {features.filter(f => f.text).map((feature) => {
              const IconComponent = getIconComponent(feature.icon);
              const isCustomIcon = feature.icon === 'custom';
              return (
                <div key={feature.id} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '36px',
                    height: '36px',
                    backgroundColor: '#10b981',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    {isCustomIcon && feature.customIcon ? (
                      <span style={{ fontSize: '16px' }}>{feature.customIcon}</span>
                    ) : IconComponent ? (
                      <IconComponent style={{ width: '18px', height: '18px', color: '#ffffff' }} />
                    ) : (
                      <span style={{ fontSize: '16px' }}>⭐</span>
                    )}
                  </div>
                  <span style={{ color: '#ffffff', fontSize: '14px', fontWeight: '500' }}>{feature.text}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default FeatureHighlights;
